import { NgIf, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, ViewChild, inject } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { PageToolbarComponent } from '@next/core-lib/components';
import { AgrCurrencyPipe, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import { PoultryContextKeys } from '../../../shared/constants';
import { Cycle } from '../../models/cycle.model';
import * as cycleActions from '../../store/actions/cycle.actions';
import {
  getCycles,
  getSelectedCycle,
  isCycleDetailedOverview,
} from '../../store/selectors/cycle.selectors';

import { CycleOverviewDetailedComponent } from '../cycle-overview-detailed/cycle-overview-detailed.component';
import { CycleOverviewSimpleComponent } from '../cycle-overview-simple/cycle-overview-simple.component';

@Component({
  selector: 'app-cycle-overview',
  templateUrl: 'cycle-overview.component.html',
  styleUrls: ['cycle-overview.component.scss'],
  providers: [AgrCurrencyPipe],
  imports: [
    PageToolbarComponent,
    MatSlideToggle,
    NgIf,
    MatIconButton,
    MatIcon,
    CycleOverviewDetailedComponent,
    CycleOverviewSimpleComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class CycleOverviewComponent implements OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private router = inject(Router);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private settingsHelper = inject(SettingsServiceHelper);

  @ViewChild('barnSelector') barnSelector: MatSelect;

  cycles$: Observable<Cycle[]> = this.store
    .select(getCycles)
    .pipe(map((cycles) => cycles.sort((a, b) => (a.cycleNumber < b.cycleNumber ? 1 : -1))));

  selectedCycle$: Observable<Cycle> = this.store.select(getSelectedCycle).pipe(
    map((cycle) => {
      this.selectedCycle = cycle;
      return cycle;
    }),
  );
  selectedCycle: Cycle;
  isDetailedOverview$: Observable<boolean> = this.store.select(isCycleDetailedOverview).pipe(
    map((v) => {
      this.isDetailedOverview = v;
      return v;
    }),
  );

  isMobile$ = this.breakpoint.ltSm$;
  isDetailedOverview: boolean;
  selectedCycleSubscription: Subscription;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.selectedCycleSubscription = this.selectedCycle$.subscribe((cycle) => {
      if (cycle?.id > 0) {
        this.settingsHelper.setSettings(PoultryContextKeys.CYCLES_SELECTED_CYCLE_ID, cycle.id);
      }
    });
  }

  addCycle() {
    this.deselectCurrentCycle();
    this.router.navigate(['/cycle', 'add']);
  }

  deselectCurrentCycle() {
    this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_CYCLE_ID);
    this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID);
  }

  editCycle(cycle: Cycle) {
    this.router.navigate(['/cycle', cycle?.id]);
  }

  deleteCycle(cycle: Cycle) {
    if (cycle?.id) {
      this.cycles$.pipe(take(1)).subscribe((cycles) => {
        this.store.dispatch(cycleActions.deleteCycleWithConfirmation({ cycle }));

        const filteredCycles = cycles.filter((c) => c.id !== cycle.id);
        if (filteredCycles.length > 0) {
          this.settingsHelper.setSettings(
            PoultryContextKeys.CYCLES_SELECTED_CYCLE_ID,
            filteredCycles[0].id,
          );
        } else {
          this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_CYCLE_ID);
        }
        this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID);
      });
    }
  }

  toggleDetailedOverview() {
    this.store.dispatch(
      cycleActions.setIsCycleDetailedOverview({ value: !this.isDetailedOverview }),
    );
  }

  ngOnDestroy() {
    if (this.selectedCycleSubscription) {
      this.selectedCycleSubscription.unsubscribe();
    }
  }
}
